import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";

import { Link, useLocation } from "react-router-dom";
import { initialPaginationData } from "../utils/constants";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import Modal, { ModalProps } from "../components/modal/Modal";
import Button from "../components/button/Button";
import Input from "../components/input/Input";
import ContextMenu, {
  ContextOption,
} from "../components/context-menu/ContextMenu";
import { AppConfigCreate, AppConfiguration } from "../utils/helpers/types";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import {
  updateAppConfig,
  createAppConfig,
  getAppConfigByModuleName,
} from "../utils/helpers/data/app-config";
import TabbedView from "../components/tabs/TabbedView";
import InheritedModule from "./inheritedModule";

const AppConfig: FunctionComponent = () => {
  let timerRef: NodeJS.Timeout;
  const { notify } = useContext(LayoutContext);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [loading, setLoading] = useState(false);
  const [appConfigs, setAppConfigs] = useState<AppConfiguration[]>([]);
  const [appConfigsCreate, setAppConfigsCreate] = useState<AppConfiguration[]>(
    []
  );

  const [appConfigOnEdit, setAppConfigOnEdit] =
    useState<AppConfiguration | null>(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const taskNameParam = searchParams.get("taskName");
  const taskIdParam = searchParams.get("id");

  const pageTitle = taskNameParam
    ? `${taskNameParam} Configuration`
    : "Application Configuration";

  const fetchAppConfigsByModuleName = async (
    moduleName: string,
    pageNumber: number = pagination.pageNumber,
    pageSize: number = pagination.pageSize
  ) => {
    setLoading(true);
    try {
      const { data, error, message } = await getAppConfigByModuleName(
        moduleName,
        pageNumber,
        pageSize
      );

      if (error && message !== "No records") {
        notify("error", `Unable to fetch app configs: ${message}`);
        return;
      }

      if (error) {
        console.error(`Unable to fetch app configs: ${message}`);
      }

      setAppConfigs(data?.records || []);
      setPagination({
        ...pagination,
        pageNumber,
        total: data?.total || 0,
      });
    } catch (error) {
      console.error("An error occurred while fetching app configs.");
    } finally {
      setLoading(false);
    }
  };

  const displayAddEditModal = (appConfig: AppConfiguration | null) => {
    setAppConfigOnEdit(appConfig);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setAppConfigOnEdit(null);
    setShowAddEditModal(false);
  };

  useEffect(() => {
    if (taskNameParam !== null) {
      fetchAppConfigsByModuleName(taskNameParam);
    }
  }, [taskIdParam, taskNameParam, pagination.pageSize]);

  useEffect(() => {
    if (taskNameParam !== null) {
      clearTimeout(timerRef);
      timerRef = setTimeout(() => {
        fetchAppConfigsByModuleName(taskNameParam);
      }, 400);
    }
  }, [taskNameParam]);

  const columns: Column<AppConfiguration>[] = [
    {
      title: "Key Label",
      dataIndex: "keyLabel",
      key: "keyLabel",
    },
    {
      title: "Key Value",
      dataIndex: "keyValue",
      key: "keyValue",
      render: (text: string | null) => {
        if (text && text.length > 30) {
          return (
            <div style={{ wordWrap: "break-word", wordBreak: "break-all" }}>
              {text}
            </div>
          );
        }
        return text || "";
      },
    },

    {
      title: "",
      render: (_: unknown, appConfig: AppConfiguration) => {
        const options: ContextOption[] = [
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(appConfig);
            },
          },
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      },
    },
  ];
  const [activeTab, setActiveTab] = useState("config");
  const isShowPageTitle = activeTab !== "inheritedModule";
  const fetchAndRefreshAppConfigs = (
    pageNumber: number = pagination.pageNumber
  ) => {
    fetchAppConfigsByModuleName(taskNameParam || "", pageNumber);
  };

  const handleRefresh = (stayOnPage: boolean) => {
    if (stayOnPage) {
      fetchAndRefreshAppConfigs(pagination.pageNumber);
    } else {
      fetchAndRefreshAppConfigs(1);
    }
  };
  return (
    <main>
      {isShowPageTitle && (
        <h1 className="title vertical-margin spaced thin">{pageTitle}</h1>
      )}
      <br />
      <br />
      <br />
      <TabbedView
        tabs={[
          {
            tabKey: "config",
            tabTitle: "Application Configuration",
            tabContent: (
              <div>
                <div className="vertical-margin flex end">
                  <Button onClick={() => displayAddEditModal(null)}>
                    Add AppConfig
                  </Button>
                </div>

                <Table
                  dataSource={appConfigs}
                  columns={columns}
                  pagination={pagination}
                  onPageChange={(pageNumber: number) =>
                    fetchAppConfigsByModuleName(taskNameParam || "", pageNumber)
                  }
                  onPageSizeChange={(pageSize) =>
                    setPagination({ ...pagination, pageSize, pageNumber: 1 })
                  }
                  loading={loading}
                  className="smaller"
                />
                <br />
                <br />
                <br />
                <br />
                <Button>
                  <Link to="/task-creation">Back to Task</Link>
                </Button>
              </div>
            ),
          },
          {
            tabKey: "inheritedModule",
            tabTitle: "InheritedModule",
            tabContent: (
              <InheritedModule taskName={taskNameParam} taskId={taskIdParam} />
            ),
          },
        ]}
        defaultKey={activeTab}
      />

      <AddEditModal
        visible={showAddEditModal}
        appConfigOnEdit={appConfigOnEdit}
        cancel={dismissAddEditModal}
        refresh={handleRefresh}
        appConfiguration={appConfigsCreate}
        moduleName={taskNameParam || ""}
      />
    </main>
  );
};

const blankAppConfig: AppConfigCreate = {
  keyLabel: "",
  keyValue: "",
  moduleName: "",
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    appConfigOnEdit: AppConfiguration | null;
    refresh: (stayOnPage: boolean) => void;
    appConfiguration: AppConfiguration[];
    cancel: () => void;
    moduleName: string;
  }
> = ({
  visible,
  cancel,
  appConfigOnEdit,
  refresh,
  appConfiguration,
  moduleName,
}) => {
  const [formData, setFormData] = useState<AppConfigCreate>(() => {
    if (visible && appConfigOnEdit) {
      return {
        keyLabel: appConfigOnEdit.keyLabel || "",
        keyValue: appConfigOnEdit.keyValue || "",
        moduleName: moduleName || "",
      };
    }
    return blankAppConfig;
  });
  const [isSaving, setIsSaving] = useState(false);

  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof AppConfigCreate, value: unknown) => {
    setFormData((prevFormData) => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      moduleName: moduleName || "",
    }));
  }, [visible, moduleName]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    let id: number | undefined;

    if (appConfigOnEdit && typeof appConfigOnEdit.id === "number") {
      id = appConfigOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message } = await updateAppConfig(id, formData);
      if (error) {
        notify("error", `Unable to update app config: ${message}`);
        setIsSaving(false);
        return;
      }
      notify("success", "Successfully updated App Config");
    } else {
      const { error, message } = await createAppConfig(formData);

      if (error) {
        notify("error", `Unable to add app config: ${message}`);
        setIsSaving(false);
        return;
      }
      notify("success", "Successfully add App Config");
      setFormData(blankAppConfig);
    }

    setIsSaving(false);
    cancel?.();
    refresh(true);
  };

  useEffect(() => {
    if (appConfigOnEdit) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        keyLabel: appConfigOnEdit.keyLabel || "",
        keyValue: appConfigOnEdit.keyValue || "",
      }));
    } else {
      setFormData(blankAppConfig);
    }
  }, [appConfigOnEdit]);

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Key Label</span>
          <Input
            value={formData.keyLabel}
            onChange={(value) => handleChange("keyLabel", value)}
            placeholder="Enter key label"
            responsive
            required
          />
        </div>
        <div className="input-group">
          <span className="question">Key Value</span>
          <Input
            value={formData.keyValue}
            onChange={(value) => handleChange("keyValue", value)}
            placeholder="Enter key Value"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <span className="question">Module Name</span>
          <Input
            value={formData.moduleName}
            onChange={(value) => handleChange("moduleName", value)}
            placeholder="Enter module name"
            responsive
            disabled
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {appConfigOnEdit
              ? "Edit App configuration"
              : "Add App configuration"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AppConfig;
